import * as React from 'react'
import { PexIcon } from '../icons/PexIcon'
import { useT } from '../../i18n'
import { dateTimePickerI18n } from '../../i18n/shared/datetime'
import {
  addPlainDays,
  easternToday,
  formatPlainDateRange,
  monthForPlainDate,
  type PlainDate,
  type PlainDateRange,
} from '../../lib/datetime'
import { cn } from '../../lib/utils'
import { useLocaleStore } from '../../store/locale'
import { Button } from './button'
import { CalendarPanel } from './date-picker'
import { POPOVER_PANEL_PLACEMENT, placePopover } from './popover-placement'

export interface DateRangePickerProps {
  value: PlainDateRange
  onChange: (value: PlainDateRange) => void
  label: string
  compactLabel?: string
  disabled?: boolean
}

type Preset = {
  key: 'today' | 'last7Days' | 'last14Days' | 'thisMonth'
  range: (today: PlainDate) => PlainDateRange
}

const PRESETS: Preset[] = [
  {
    key: 'today',
    range: (today) => ({ start: today, end: today }),
  },
  {
    key: 'last7Days',
    range: (today) => ({ start: addPlainDays(today, -6), end: today }),
  },
  {
    key: 'last14Days',
    range: (today) => ({ start: addPlainDays(today, -13), end: today }),
  },
  {
    key: 'thisMonth',
    range: (today) => ({
      start: `${today.slice(0, 7)}-01` as PlainDate,
      end: today,
    }),
  },
]

export function DateRangePicker({
  value,
  onChange,
  label,
  compactLabel,
  disabled = false,
}: DateRangePickerProps) {
  const t = useT(dateTimePickerI18n)
  const locale = useLocaleStore((state) => state.locale)
  const [open, setOpen] = React.useState(false)
  const [draft, setDraft] = React.useState<PlainDateRange>(value)
  const [month, setMonth] = React.useState(() =>
    monthForPlainDate(value.start ?? value.end ?? easternToday()),
  )
  const triggerRef = React.useRef<HTMLButtonElement>(null)
  const rootRef = React.useRef<HTMLDivElement>(null)
  const id = React.useId()
  const labelId = `${id}-label`

  const close = React.useCallback(() => {
    setOpen(false)
    triggerRef.current?.focus()
  }, [])

  React.useEffect(() => {
    if (!open) return
    const onPointerDown = (event: PointerEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) close()
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') close()
    }
    document.addEventListener('pointerdown', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('pointerdown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [close, open])

  const openPicker = () => {
    setDraft(value)
    setMonth(monthForPlainDate(value.start ?? value.end ?? easternToday()))
    setOpen(true)
  }

  const selectDate = (date: PlainDate) => {
    setDraft((current) => {
      if (!current.start || current.end) return { start: date, end: null }
      if (date < current.start) return { start: date, end: current.start }
      return { start: current.start, end: date }
    })
  }

  const displayValue = formatPlainDateRange(value, locale)

  return (
    <div ref={rootRef} className="relative flex flex-col gap-[var(--field-gap)]">
      <span
        id={labelId}
        className={cn(
          'text-[length:var(--field-label-size)] font-semibold text-[var(--field-label-text)]',
          compactLabel && 'sr-only',
        )}
      >
        {label}
      </span>
      <button
        ref={triggerRef}
        id={id}
        type="button"
        disabled={disabled}
        aria-labelledby={labelId}
        aria-haspopup="dialog"
        aria-expanded={open}
        className="flex w-full min-h-[var(--field-min-touch)] items-center gap-2 rounded-[var(--field-radius)] border border-[var(--field-border)] bg-[var(--field-bg)] px-[var(--field-padding-x)] text-left text-[length:var(--field-font-size)] text-[var(--field-text)] focus-visible:border-[var(--field-focus-border)] focus-visible:outline-none focus-visible:shadow-[var(--field-focus-shadow)] disabled:cursor-not-allowed disabled:bg-[var(--field-disabled-bg)] disabled:text-[var(--field-disabled-text)] sm:h-[var(--field-height)] sm:min-h-[var(--field-height)]"
        onClick={openPicker}
      >
        {!compactLabel && <PexIcon name="calendar" size="sm" />}
        {compactLabel && (
          <span aria-hidden="true" className="shrink-0 font-semibold">
            {compactLabel}:
          </span>
        )}
        <span
          className={cn(
            'flex-1',
            !displayValue && 'text-[var(--field-placeholder)]',
          )}
        >
          {displayValue || t.emptyValue}
        </span>
        <PexIcon name="caretDown" size="xs" />
      </button>
      {open && (
        <div
          ref={placePopover}
          role="dialog"
          aria-label={t.chooseRange(label)}
          className={cn(
            'absolute left-0 z-[var(--z-dropdown)] w-[min(22rem,calc(100vw-1.5rem))] rounded-[var(--surface-lifted-radius)] border border-[var(--surface-lifted-border)] bg-[var(--surface-lifted-bg)] p-3 shadow-[var(--surface-lifted-shadow)]',
            POPOVER_PANEL_PLACEMENT,
          )}
        >
          <div
            role="group"
            aria-label={t.presetsLabel}
            className="mb-3 flex flex-wrap gap-1.5 border-b border-[var(--color-border)] pb-3"
          >
            {PRESETS.map((preset) => (
              <Button
                key={preset.key}
                type="button"
                variant="outline"
                size="sm"
                className="min-h-[var(--field-min-touch)] sm:h-[var(--field-height)] sm:min-h-[var(--field-height)]"
                onClick={() => {
                  const nextRange = preset.range(easternToday())
                  setDraft(nextRange)
                  setMonth(monthForPlainDate(nextRange.end as PlainDate))
                }}
              >
                {t.presets[preset.key]}
              </Button>
            ))}
          </div>
          <CalendarPanel
            month={month}
            onMonthChange={setMonth}
            selection={draft}
            onSelect={selectDate}
            onEscape={close}
          />
          <div className="mt-3 flex justify-between border-t border-[var(--color-border)] pt-3">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="min-h-[var(--field-min-touch)] sm:h-[var(--field-height)] sm:min-h-[var(--field-height)]"
              onClick={() => setDraft({ start: null, end: null })}
            >
              {t.clear}
            </Button>
            <Button
              type="button"
              size="sm"
              className="min-h-[var(--field-min-touch)] sm:h-[var(--field-height)] sm:min-h-[var(--field-height)]"
              disabled={Boolean(draft.start) !== Boolean(draft.end)}
              onClick={() => {
                onChange(draft)
                close()
              }}
            >
              {t.apply}
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}
